import { useState, useRef, useEffect } from "react";
import { MessageSquarePlus, Sparkles, ArrowRight, User } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const SAMPLE_NUDGES_APP = [
  "Add a settings screen with light/dark mode and notifications.",
  "Move auth from JWT to Sign in with Apple.",
  "Add CSV export to the main feed screen.",
];
const SAMPLE_NUDGES_GAME = [
  "Make it 3D — switch to SceneKit.",
  "Add a power-up that doubles score for 5 seconds.",
  "Tighten the gameplay loop — drop the easiest level, raise difficulty.",
];

export default function FollowUpPanel({
  blueprint,
  onUpdate,
  endpoint, // "/prompt/refine" or "/games/refine"
  variant = "app", // "app" | "game"
  onUsageRefresh,
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const turns = blueprint?.refine_turns || [];
  const samples = variant === "game" ? SAMPLE_NUDGES_GAME : SAMPLE_NUDGES_APP;
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [turns.length, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const follow = text.trim();
    if (follow.length < 4) {
      toast.error("Tell me a little more about the change you want.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, {
        blueprint,
        follow_up: follow,
        history: turns,
      });
      onUpdate(data);
      setText("");
      toast.success("Blueprint refined ✨");
      onUsageRefresh?.();
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.error(err.response?.data?.detail || "Out of prompts this month.");
      else toast.error(err.response?.data?.detail || "Refinement failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card-soft p-5 md:p-6 bg-gradient-to-br from-cream via-paper to-saffron/5" data-testid={`followup-panel-${variant}`}>
      <div className="flex items-center gap-2 mb-3 text-marigold">
        <MessageSquarePlus className="w-4 h-4" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ink">
          Keep iterating · {turns.length} refinement{turns.length === 1 ? "" : "s"}
        </h3>
      </div>

      {turns.length > 0 && (
        <div
          ref={listRef}
          className="mb-4 max-h-44 overflow-y-auto space-y-2 pr-1"
          data-testid={`followup-history-${variant}`}
        >
          {turns.map((t, i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <span className="w-6 h-6 rounded-full bg-ink text-cream flex-shrink-0 flex items-center justify-center">
                <User className="w-3 h-3" />
              </span>
              <div className="flex-1 bg-paper border border-border rounded-2xl rounded-tl-none px-3 py-2 text-ink">
                {t}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex items-start gap-2 text-sm" data-testid={`followup-busy-${variant}`}>
              <span className="w-6 h-6 rounded-full bg-marigold/20 text-marigold flex-shrink-0 flex items-center justify-center">
                <Sparkles className="w-3 h-3 animate-pulse" />
              </span>
              <div className="flex-1 bg-marigold/10 border border-marigold/30 rounded-2xl rounded-tl-none px-3 py-2 text-ink2 italic">
                Refining your {variant === "game" ? "game" : "app"}…
              </div>
            </div>
          )}
        </div>
      )}

      <form onSubmit={send} className="flex flex-col gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          maxLength={2000}
          disabled={busy}
          placeholder={variant === "game"
            ? "Add a feature, change a mechanic, swap the engine — say it in plain English."
            : "Add a screen, change a flow, evolve the data model — say it in plain English."}
          className="w-full bg-paper border-2 border-ink rounded-2xl px-4 py-3 text-ink text-sm placeholder:text-ink2/60 outline-none focus:border-marigold transition disabled:opacity-50"
          data-testid={`followup-textarea-${variant}`}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") send(e);
          }}
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-ink2">
            <kbd className="px-1.5 py-0.5 rounded bg-paper border border-border font-mono text-[10px]">⌘ Enter</kbd> to send · {text.length} / 2000
          </div>
          <button
            type="submit"
            disabled={busy || text.trim().length < 4}
            className="btn-primary text-sm py-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`followup-send-${variant}`}
          >
            <Sparkles className="w-4 h-4" /> {busy ? "Refining…" : "Send follow-up"}
            {!busy && <ArrowRight className="w-3.5 h-3.5" />}
          </button>
        </div>
      </form>

      {turns.length === 0 && !busy && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid={`followup-samples-${variant}`}>
          <span className="text-[10px] uppercase tracking-wider text-ink2 self-center mr-1 font-semibold">Try:</span>
          {samples.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setText(s)}
              className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-ink hover:bg-ink hover:text-cream transition"
              data-testid={`followup-sample-${variant}-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
